import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaMinus, FaPaperclip, FaPlus, FaSearch, FaStore, FaTrash, FaTruck, FaUserCheck } from 'react-icons/fa';
import { useCart } from '../context/CartContext';
import { SHIPPING_ZONES, TIME_RANGES } from '../data/shipping';
import { formatShippingSchedule, getAvailableShippingTimeRanges } from '../utils/shippingSchedule';
import { FamilyMember } from '../types';
import { getProductImageUrls } from '../utils';
import jsPDF from 'jspdf';
import styles from './styles/CheckoutPage.module.css';
import { BRAND } from '../data/brand';

declare const Decidir: any;

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const PAYWAY_PUBLIC_KEY = process.env.REACT_APP_PAYWAY_PUBLIC_KEY || '';
const PAYWAY_ENV = process.env.REACT_APP_PAYWAY_ENV || 'sandbox';
const NORMALIZED_PAYWAY_ENV = PAYWAY_ENV.trim().toLowerCase();
const PAYWAY_IS_PROD = ['prod', 'produccion', 'production'].includes(NORMALIZED_PAYWAY_ENV);
const PAYWAY_SDK_URL = PAYWAY_IS_PROD
    ? 'https://ventasonline.payway.com.ar/api/v2'
    : 'https://developers.decidir.com/api/v2';
const COMPROBANTE_MAX_BYTES = 6 * 1024 * 1024;
const COMPROBANTE_ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'pdf'];

const toDateInputValue = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const getMinDeliveryDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return toDateInputValue(tomorrow);
};

const isSundayDateValue = (value: string) => {
    if (!value) return false;
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day).getDay() === 0;
};

const formatDeliveryDate = (value: string) => {
    if (!value) return 'A confirmar';
    const [year, month, day] = value.split('-').map(Number);
    return new Intl.DateTimeFormat('es-AR', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        year: 'numeric'
    }).format(new Date(year, month - 1, day));
};

type DeliveryMode = 'Envio' | 'Retiro';
type CardType = 'debit' | 'credit';
type PaywayCardBrand = 'visa' | 'mastercard' | 'amex' | 'diners' | 'cabal' | 'unknown';

interface Local {
    locales_id: number;
    nombre_local: string;
}

interface AddressSuggestion {
    placeId: string;
    description: string;
    mainText?: string;
    secondaryText?: string;
}

interface DetectedShippingZone {
    shippingZoneId: string;
    mapZoneName: string;
    confidence: 'high' | 'medium';
}

interface ConfirmedOrder {
    id: number | string;
    customer: string;
    total: number;
    subtotal: number;
    shippingCost: number;
    delivery: string;
    payment: string;
    deliveryDate: string;
    deliveryTime: string;
    observations: string;
    items: Array<{ name: string; quantity: number; total: number }>;
}

const getPaywayCardBrand = (bin: string): PaywayCardBrand => {
    const digits = bin.replace(/\D/g, '');
    const firstTwo = digits.slice(0, 2);
    const firstSix = Number((digits + '000000').slice(0, 6));

    if (digits.startsWith('6042') || digits.startsWith('589657') || digits.startsWith('603522')) return 'cabal';
    if (digits.startsWith('4')) return 'visa';
    if (digits.startsWith('5') || (firstSix >= 222100 && firstSix <= 272099)) return 'mastercard';
    if (firstTwo === '34' || firstTwo === '37') return 'amex';
    if (['30', '36', '38', '39'].includes(firstTwo)) return 'diners';
    return 'unknown';
};

const getPaywayPaymentMethodId = (bin: string, cardType: CardType) => {
    const brand = getPaywayCardBrand(bin);
    if (brand === 'visa') return cardType === 'debit' ? 31 : 1;
    if (brand === 'mastercard') return cardType === 'debit' ? 105 : 104;
    if (brand === 'cabal') return cardType === 'debit' ? 108 : 63;
    if (brand === 'amex') return cardType === 'credit' ? 65 : null;
    if (brand === 'diners') return cardType === 'credit' ? 59 : null;
    return null;
};

const combineDeliveryAddress = (address: string, additionalInfo: string) =>
    [address.trim(), additionalInfo.trim()].filter(Boolean).join(' ');

const CheckoutPage: React.FC = () => {
    const { cart, clearCart, removeFromCart, decreaseQuantity, addToCart } = useCart();
    const navigate = useNavigate();
    const cardFormRef = useRef<HTMLDivElement>(null);

    const [formData, setFormData] = useState({
        dni: '',
        nombre: '',
        direccion: '',
        pisoDepartamento: '',
        telefono: '',
        email: '',
        fechaEntrega: '',
        horarioEntrega: '',
        metodoPago: 'Efectivo',
        observaciones: '',
        banco: '',
        envioId: '',
        tipoEntrega: 'Envio' as DeliveryMode,
        id_local: ''
    });

    const [card, setCard] = useState({
        card_number: '',
        card_expiration_month: '',
        card_expiration_year: '',
        security_code: '',
        card_holder_name: '',
        card_holder_doc_number: '',
        card_type: '' as CardType | ''
    });

    const [locales, setLocales] = useState<Local[]>([]);
    const [shippingCost, setShippingCost] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [familySearch, setFamilySearch] = useState('');
    const [familyResults, setFamilyResults] = useState<FamilyMember[]>([]);
    const [familyLoading, setFamilyLoading] = useState(false);
    const [selectedMember, setSelectedMember] = useState<FamilyMember | null>(null);
    const [transferReceipt, setTransferReceipt] = useState<File | null>(null);
    const [transferReceiptError, setTransferReceiptError] = useState('');
    const [transferReceiptUploadedAt, setTransferReceiptUploadedAt] = useState('');
    const [detectedZone, setDetectedZone] = useState<DetectedShippingZone | null>(null);
    const [registerModalOpen, setRegisterModalOpen] = useState(false);
    const [registerLoading, setRegisterLoading] = useState(false);
    const [registerError, setRegisterError] = useState('');
    const [registerForm, setRegisterForm] = useState<Partial<FamilyMember>>({});
    const [addressSuggestions, setAddressSuggestions] = useState<AddressSuggestion[]>([]);
    const [addressFocused, setAddressFocused] = useState(false);
    const [zoneWasManuallySelected, setZoneWasManuallySelected] = useState(false);
    const [addressLookupMessage, setAddressLookupMessage] = useState('');
    const [confirmedOrder, setConfirmedOrder] = useState<ConfirmedOrder | null>(null);

    const subtotal = cart.reduce((sum, item) => sum + (item.Precio * item.quantity), 0);
    const total = subtotal + shippingCost;
    const isDelivery = formData.tipoEntrega === 'Envio';
    const pickupLocales = locales.filter(local => {
        const normalizedName = local.nombre_local
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase();
        return !normalizedName.includes('fabrica');
    });
    const selectedLocal = pickupLocales.find(local => String(local.locales_id) === String(formData.id_local));
    const minDeliveryDate = getMinDeliveryDate();
    const availableDeliveryTimeRanges = getAvailableShippingTimeRanges(
        formData.fechaEntrega,
        detectedZone?.mapZoneName,
        formData.envioId
    );
    const availableTimeRanges = isDelivery ? availableDeliveryTimeRanges : TIME_RANGES;
    const deliveryScheduleDescription = formatShippingSchedule(
        detectedZone?.mapZoneName,
        formData.envioId
    );

    const downloadConfirmedOrder = () => {
        if (!confirmedOrder) return;
        const doc = new jsPDF();
        let y = 20;
        const addLine = (text: string, gap = 8) => {
            const lines = doc.splitTextToSize(text, 175);
            doc.text(lines, 18, y);
            y += lines.length * 6 + gap;
        };
        doc.setFontSize(18);
        doc.text(BRAND.name, 18, y);
        y += 10;
        doc.setFontSize(15);
        addLine(`Pedido confirmado #${confirmedOrder.id}`);
        doc.setFontSize(11);
        addLine(`Cliente: ${confirmedOrder.customer}`);
        addLine(`Entrega: ${confirmedOrder.delivery}`);
        addLine(`Dia y horario: ${formatDeliveryDate(confirmedOrder.deliveryDate)} - ${confirmedOrder.deliveryTime}`);
        addLine(`Pago: ${confirmedOrder.payment}`);
        addLine(`Observaciones: ${confirmedOrder.observations || 'Sin observaciones'}`);
        y += 2;
        doc.setFontSize(13);
        addLine('Resumen');
        doc.setFontSize(11);
        confirmedOrder.items.forEach(item => addLine(`${item.quantity} x ${item.name} - $${item.total.toLocaleString('es-AR')}`, 3));
        y += 3;
        addLine(`Subtotal: $${confirmedOrder.subtotal.toLocaleString('es-AR')}`, 3);
        addLine(`Envio: $${confirmedOrder.shippingCost.toLocaleString('es-AR')}`, 3);
        doc.setFontSize(13);
        addLine(`Total: $${confirmedOrder.total.toLocaleString('es-AR')}`);
        doc.save(`pedido-${confirmedOrder.id}.pdf`);
    };

    useEffect(() => {
        fetch(`${API_URL}/locales`)
            .then(res => res.json())
            .then(data => setLocales(Array.isArray(data) ? data : []))
            .catch(err => console.error('Error cargando locales', err));
    }, []);

    useEffect(() => {
        if (!isDelivery || !formData.envioId) {
            setShippingCost(0);
            return;
        }

        const zone = SHIPPING_ZONES.find(z => z.id === formData.envioId);
        if (!zone) {
            setShippingCost(0);
            return;
        }

        setShippingCost(subtotal >= zone.minPurchaseForFree ? 0 : zone.cost);
    }, [formData.envioId, isDelivery, subtotal]);

    useEffect(() => {
        const address = formData.direccion.trim();
        if (!isDelivery || !addressFocused || address.length < 3) {
            setAddressSuggestions([]);
            return;
        }
        const controller = new AbortController();
        const timer = window.setTimeout(async () => {
            try {
                const response = await fetch(`${API_URL}/shipping-address/suggestions?q=${encodeURIComponent(address)}`, { signal: controller.signal });
                const data = await response.json();
                setAddressSuggestions(Array.isArray(data) ? data : []);
            } catch (err) {
                if (!controller.signal.aborted) setAddressSuggestions([]);
            }
        }, 300);
        return () => {
            controller.abort();
            window.clearTimeout(timer);
        };
    }, [addressFocused, formData.direccion, isDelivery]);

    const detectZone = useCallback(async (address: string, force = false) => {
        setAddressLookupMessage('Buscando calle y altura en el nomenclador municipal...');
        try {
            const response = await fetch(`${API_URL}/shipping-zone/detect?address=${encodeURIComponent(address)}`);
            const data = await response.json();
            if (response.ok && data?.detected && data?.shippingZoneId && (force || !zoneWasManuallySelected)) {
                setDetectedZone({
                    shippingZoneId: data.shippingZoneId,
                    mapZoneName: data.mapZoneName || data.formattedAddress || 'Zona detectada',
                    confidence: 'high'
                });
                setFormData(prev => ({ ...prev, envioId: data.shippingZoneId }));
                setAddressLookupMessage('Direccion validada con el nomenclador municipal.');
                return;
            }
            setAddressLookupMessage(data?.error || 'No pudimos determinar la zona. Podes seleccionarla manualmente.');
        } catch (err) {
            setAddressLookupMessage('No pudimos consultar el nomenclador. Podes seleccionar la zona manualmente.');
        }
    }, [zoneWasManuallySelected]);

    const selectAddressSuggestion = (suggestion: AddressSuggestion) => {
        setFormData(prev => ({ ...prev, direccion: suggestion.description }));
        setAddressSuggestions([]);
        setAddressFocused(false);
        setZoneWasManuallySelected(false);
        void detectZone(suggestion.description, true);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        if (name === 'direccion') {
            setDetectedZone(null);
            setZoneWasManuallySelected(false);
            setAddressLookupMessage('');
        }
        if (name === 'envioId') {
            setZoneWasManuallySelected(true);
            setDetectedZone(null);
        }
        if (name === 'fechaEntrega') {
            if (value && value < minDeliveryDate) {
                setError('La fecha tiene que ser posterior a hoy.');
                setFormData(prev => ({ ...prev, fechaEntrega: '' }));
                return;
            }
            if (isSundayDateValue(value)) {
                setError('Los domingos no estan disponibles para entregas o retiros.');
                setFormData(prev => ({ ...prev, fechaEntrega: '' }));
                return;
            }
            if (isDelivery && !formData.envioId) {
                setError('Primero elegí o detectá la zona de envío.');
                setFormData(prev => ({ ...prev, fechaEntrega: '', horarioEntrega: '' }));
                return;
            }
            if (isDelivery && value && getAvailableShippingTimeRanges(
                value,
                detectedZone?.mapZoneName,
                formData.envioId
            ).length === 0) {
                setError('No realizamos envíos a esa zona el día seleccionado. Elegí uno de los días indicados.');
                setFormData(prev => ({ ...prev, fechaEntrega: '', horarioEntrega: '' }));
                return;
            }
            setError('');
        }
        setFormData(prev => ({
            ...prev,
            [name]: value,
            ...(name === 'envioId' || name === 'fechaEntrega' ? { horarioEntrega: '' } : {}),
            ...(name === 'envioId' ? { fechaEntrega: '' } : {})
        }));
        if (name === 'metodoPago') {
            setTransferReceiptError('');
            if (value !== 'Transferencia') {
                setTransferReceipt(null);
                setTransferReceiptUploadedAt('');
            }
        }
    };

    const handleCardChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setCard(prev => ({ ...prev, [name]: value }));
    };

    const getCardBin = () => {
        const typedDigits = card.card_number.replace(/\D/g, '');
        return typedDigits.slice(0, 6);
    };

    const validateTransferReceipt = (file: File) => {
        const extension = file.name.split('.').pop()?.toLowerCase() || '';
        if (!COMPROBANTE_ALLOWED_EXTENSIONS.includes(extension)) {
            return 'El comprobante debe ser JPG, PNG, WEBP o PDF.';
        }
        if (file.size > COMPROBANTE_MAX_BYTES) {
            return 'El comprobante no puede superar los 6 MB.';
        }
        return '';
    };

    const uploadTransferReceipt = async (orderUuid: string, file: File) => {
        const receiptForm = new FormData();
        receiptForm.append('comprobante', file);

        const receiptResponse = await fetch(`${API_URL}/admin/orders/${orderUuid}/comprobante`, {
            method: 'POST',
            body: receiptForm
        });

        if (!receiptResponse.ok) {
            const receiptError = await receiptResponse.json().catch(() => null);
            throw new Error(receiptError?.error || 'El pedido fue creado, pero no se pudo subir el comprobante.');
        }

        return receiptResponse.json();
    };

    const handleReceiptChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null;
        setError('');
        setTransferReceiptError('');
        setTransferReceiptUploadedAt('');

        if (!file) {
            setTransferReceipt(null);
            return;
        }

        const validation = validateTransferReceipt(file);
        if (validation) {
            setTransferReceipt(null);
            e.target.value = '';
            setTransferReceiptError(validation);
            return;
        }

        setTransferReceipt(file);
        setTransferReceiptUploadedAt(new Intl.DateTimeFormat('es-AR', {
            dateStyle: 'short',
            timeStyle: 'short'
        }).format(new Date()));
    };

    const searchFamilyMember = useCallback(async (term = familySearch.trim()) => {
        if (term.length < 2) return;
        setFamilyLoading(true);
        try {
            const res = await fetch(`${API_URL}/miembros/search?q=${encodeURIComponent(term)}`);
            const data = await res.json();
            setFamilyResults(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error buscando cliente fiel', err);
            setFamilyResults([]);
        } finally {
            setFamilyLoading(false);
        }
    }, [familySearch]);

    useEffect(() => {
        const term = familySearch.trim();
        if (term.length < 2) {
            setFamilyResults([]);
            return;
        }

        const timer = window.setTimeout(() => {
            searchFamilyMember(term);
        }, 350);

        return () => window.clearTimeout(timer);
    }, [familySearch, searchFamilyMember]);

    const selectFamilyMember = (member: FamilyMember) => {
        const fullName = `${member.nombre || ''} ${member.apellido || ''}`.trim();
        setSelectedMember(member);
        setFormData(prev => ({
            ...prev,
            dni: member.dni || prev.dni,
            nombre: fullName || prev.nombre,
            direccion: member.direccion || prev.direccion,
            pisoDepartamento: '',
            telefono: member.telefono || prev.telefono,
            email: member.email || prev.email
        }));
        setFamilySearch('');
        setFamilyResults([]);
    };

    const openRegisterModal = () => {
        const [firstName = '', ...lastNameParts] = formData.nombre.trim().split(/\s+/);
        setRegisterError('');
        setRegisterForm({
            dni: formData.dni,
            nombre: firstName,
            apellido: lastNameParts.join(' '),
            direccion: combineDeliveryAddress(formData.direccion, formData.pisoDepartamento),
            telefono: formData.telefono,
            email: formData.email,
            fecha_nacimiento: '',
            sexo: ''
        });
        setRegisterModalOpen(true);
    };

    const handleRegisterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setRegisterForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleRegisterSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setRegisterError('');
        setRegisterLoading(true);
        try {
            const res = await fetch(`${API_URL}/miembros/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(registerForm)
            });
            const data = await res.json().catch(() => null);
            if (!res.ok) {
                throw new Error(data?.error || 'No se pudo registrar el cliente fiel.');
            }

            const member = data?.member as FamilyMember;
            if (member) {
                selectFamilyMember(member);
            }
            setRegisterModalOpen(false);
        } catch (err) {
            setRegisterError(err instanceof Error ? err.message : 'No se pudo registrar el cliente fiel.');
        } finally {
            setRegisterLoading(false);
        }
    };

    const handleDeliveryModeChange = (mode: DeliveryMode) => {
        setFormData(prev => ({
            ...prev,
            tipoEntrega: mode,
            envioId: mode === 'Envio' ? prev.envioId : '',
            id_local: mode === 'Retiro' ? prev.id_local : ''
        }));
    };

    const validateCheckout = () => {
        if (formData.fechaEntrega && formData.fechaEntrega < minDeliveryDate) return 'La fecha tiene que ser posterior a hoy';
        if (formData.fechaEntrega && isSundayDateValue(formData.fechaEntrega)) return 'Los domingos no estan disponibles para entregas o retiros';
        if (cart.length === 0) return 'El carrito esta vacio';
        if (!formData.dni.trim()) return 'Ingresá el DNI';
        if (!formData.nombre.trim()) return 'Ingresá el nombre completo';
        if (!formData.email.trim()) return 'Ingresá el email';
        if (!formData.telefono.trim()) return 'Ingresá el telefono';
        if (!formData.fechaEntrega) return 'Elegí la fecha de entrega o retiro';
        if (!formData.horarioEntrega) return 'Elegí el horario';
        if (isDelivery && !formData.direccion.trim()) return 'Ingresá la direccion de entrega';
        if (isDelivery && !formData.envioId) return 'Elegí la zona de envio';
        if (isDelivery && formData.fechaEntrega) {
            const allowedRanges = getAvailableShippingTimeRanges(
                formData.fechaEntrega,
                detectedZone?.mapZoneName,
                formData.envioId
            );
            if (allowedRanges.length === 0) return 'No realizamos envíos a esa zona el día seleccionado';
            if (formData.horarioEntrega && !allowedRanges.includes(formData.horarioEntrega)) {
                return 'El horario elegido no está disponible para esa zona y fecha';
            }
        }
        if (!isDelivery && !formData.id_local) return 'Elegí el local de retiro';
        if (!formData.metodoPago) return 'Elegí el metodo de pago';
        if (formData.metodoPago === 'Payway' && !card.card_type) return 'ElegÃ­ si la tarjeta es de debito o credito';
        if (formData.metodoPago === 'Payway' && getCardBin().length === 6 && card.card_type && !getPaywayPaymentMethodId(getCardBin(), card.card_type)) {
            return 'No pudimos identificar esa tarjeta para el tipo elegido';
        }
        if (formData.metodoPago === 'Transferencia' && transferReceiptError) return transferReceiptError;
        if (formData.metodoPago === 'Transferencia' && !transferReceipt) {
            setTransferReceiptError('Adjunta el comprobante de transferencia.');
            return 'Adjunta el comprobante de transferencia';
        }
        if (transferReceipt) {
            const receiptValidation = validateTransferReceipt(transferReceipt);
            if (receiptValidation) {
                setTransferReceiptError(receiptValidation);
                return receiptValidation;
            }
        }
        return '';
    };

    const tokenizarTarjeta = (): Promise<{ token: string; bin: string; binSource: string; sdkBinMatchesTyped: boolean; paymentMethodId: number; cardType: CardType }> => {
        return new Promise((resolve, reject) => {
            if (typeof Decidir === 'undefined') {
                reject(new Error('No se pudo cargar el formulario de tarjeta. Refresca la pagina.'));
                return;
            }
            if (!PAYWAY_PUBLIC_KEY) {
                reject(new Error('Falta configurar el pago con tarjeta.'));
                return;
            }
            const selectedCardType = card.card_type;
            if (!selectedCardType) {
                reject(new Error('Elegí si la tarjeta es de debito o credito.'));
                return;
            }

            const decidir = new Decidir(PAYWAY_SDK_URL);
            decidir.setPublishableKey(PAYWAY_PUBLIC_KEY);
            decidir.setTimeout(10000);

            const form = cardFormRef.current?.closest('form');
            if (!form) {
                reject(new Error('No se encontro el formulario de tarjeta.'));
                return;
            }

            decidir.createToken(form, (status: number, response: any) => {
                if (status !== 200 && status !== 201) {
                    const msg = response?.error?.[0]?.message
                        || (Array.isArray(response?.validation_errors) && response.validation_errors[0]?.message)
                        || response?.message
                        || 'Datos de tarjeta invalidos';
                    reject(new Error(`Pago rechazado: ${msg}`));
                    return;
                }

                const token = response?.id || response?.token;
                const responseBin = String(response?.bin || response?.card_number_bin || response?.cardholder?.bin || '').replace(/\D/g, '');
                const typedBin = getCardBin();
                const bin = (typedBin || responseBin).slice(0, 6);
                const binSource = typedBin ? 'typed' : 'sdk';
                const sdkBinMatchesTyped = !responseBin || !typedBin || responseBin.slice(0, 6) === typedBin.slice(0, 6);
                if (!token) {
                    reject(new Error('No se pudo validar la tarjeta.'));
                    return;
                }
                if (bin.length !== 6) {
                    reject(new Error('Revisa el numero de tarjeta antes de confirmar.'));
                    return;
                }
                const paymentMethodId = getPaywayPaymentMethodId(bin, selectedCardType);
                if (!paymentMethodId) {
                    reject(new Error('No pudimos identificar esa tarjeta para el tipo elegido. Revisa si es debito o credito.'));
                    return;
                }
                resolve({ token, bin, binSource, sdkBinMatchesTyped, paymentMethodId, cardType: selectedCardType });
            });
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        const validation = validateCheckout();
        if (validation) {
            setError(validation);
            return;
        }

        setIsSubmitting(true);

        const deliveryAddress = isDelivery
            ? combineDeliveryAddress(formData.direccion, formData.pisoDepartamento)
            : (selectedLocal ? `Retiro en ${selectedLocal.nombre_local}` : 'Retiro por local');

        const orderData = {
            ...formData,
            pisoDepartamento: undefined,
            direccion: deliveryAddress,
            cartItems: cart,
            montoTotal: total,
            costoEnvio: shippingCost,
            tipoPedido: formData.tipoEntrega,
            envioDomicilio: isDelivery ? 'Si' : 'No',
            medio: 'Pagina Web',
            id_local: isDelivery ? null : Number(formData.id_local),
            pagado: 'No'
        };

        try {
            const response = await fetch(`${API_URL}/orders`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(orderData)
            });

            if (!response.ok) {
                const errData = await response.json().catch(() => null);
                throw new Error(errData?.error || 'Error al procesar el pedido');
            }

            const orderResult = await response.json();
            const orderUuid = orderResult.uuid;
            const showConfirmation = () => {
                setConfirmedOrder({
                    id: orderResult.orderId,
                    customer: formData.nombre,
                    total,
                    subtotal,
                    shippingCost,
                    delivery: deliveryAddress,
                    payment: formData.metodoPago,
                    deliveryDate: formData.fechaEntrega,
                    deliveryTime: formData.horarioEntrega,
                    observations: formData.observaciones.trim(),
                    items: cart.map(item => ({
                        name: item.NombresProductos,
                        quantity: item.quantity,
                        total: item.Precio * item.quantity
                    }))
                });
                clearCart();
                window.scrollTo({ top: 0, behavior: 'smooth' });
            };

            if (formData.metodoPago === 'Payway') {
                const { token, bin, binSource, sdkBinMatchesTyped, paymentMethodId, cardType } = await tokenizarTarjeta();
                const payRes = await fetch(`${API_URL}/create_payway_payment`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        token,
                        bin,
                        payment_method_id: paymentMethodId,
                        cardType,
                        binSource,
                        sdkBinMatchesTyped,
                        frontendPaywayEnv: PAYWAY_ENV,
                        installments: 1,
                        items: cart,
                        costoEnvio: shippingCost,
                        orderUuid,
                        comprador: {
                            nombre: formData.nombre,
                            email: formData.email,
                            dni: formData.dni,
                            telefono: formData.telefono,
                            direccion: deliveryAddress
                        }
                    })
                });

                const payData = await payRes.json();
                if (!payRes.ok || payData.status !== 'approved') {
                    throw new Error(payData.details || payData.error || 'El pago fue rechazado');
                }

                showConfirmation();
                return;
            }

            if (formData.metodoPago === 'Transferencia' && transferReceipt) {
                await uploadTransferReceipt(orderUuid, transferReceipt);
                showConfirmation();
                return;
            }

            showConfirmation();
        } catch (err: any) {
            console.error(err);
            setError(err.message || 'Error desconocido');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (confirmedOrder) {
        return (
            <main className={`${styles['checkout-container']} ${styles['confirmation-page']}`}>
                <section className={styles['confirmation-card']}>
                    <div className={styles['confirmation-check']} aria-hidden="true">✓</div>
                    <p className={styles['confirmation-kicker']}>Pedido confirmado</p>
                    <h1>Gracias por tu compra</h1>
                    <p className={styles['confirmation-customer']}>{confirmedOrder.customer}</p>
                    <p>Recibimos tu pedido correctamente.</p>
                    <div className={styles['confirmation-order-id']}>Pedido #{confirmedOrder.id}</div>
                    <div className={styles['confirmation-summary']}>
                        <h2>Resumen del pedido</h2>
                        {confirmedOrder.items.map((item, index) => (
                            <div className={styles['confirmation-line']} key={`${item.name}-${index}`}>
                                <span>{item.quantity} x {item.name}</span>
                                <strong>${item.total.toLocaleString()}</strong>
                            </div>
                        ))}
                        <div className={styles['confirmation-line']}><span>Subtotal</span><strong>${confirmedOrder.subtotal.toLocaleString()}</strong></div>
                        <div className={styles['confirmation-line']}><span>Envio</span><strong>${confirmedOrder.shippingCost.toLocaleString()}</strong></div>
                        <div className={`${styles['confirmation-line']} ${styles['confirmation-total']}`}><span>Total</span><strong>${confirmedOrder.total.toLocaleString()}</strong></div>
                    </div>
                    <div className={styles['confirmation-details']}>
                        <span><strong>Entrega:</strong> {confirmedOrder.delivery}</span>
                        <span><strong>Dia:</strong> {formatDeliveryDate(confirmedOrder.deliveryDate)}</span>
                        <span><strong>Horario:</strong> {confirmedOrder.deliveryTime}</span>
                        <span><strong>Pago:</strong> {confirmedOrder.payment}</span>
                        <span><strong>Observaciones:</strong> {confirmedOrder.observations || 'Sin observaciones'}</span>
                    </div>
                    <div className={styles['confirmation-actions']}>
                        <button type="button" className={styles['download-order-btn']} onClick={downloadConfirmedOrder}>Descargar pedido</button>
                        <button type="button" className={styles['checkout-btn']} onClick={() => navigate('/productos')}>Seguir comprando</button>
                    </div>
                </section>
            </main>
        );
    }

    if (cart.length === 0) {
        return (
            <div className={`${styles['checkout-container']} ${styles['checkout-empty']}`}>
                <h2>Tu carrito esta vacio</h2>
                <button onClick={() => navigate('/productos')} className={styles['checkout-btn']}>
                    Ver productos
                </button>
            </div>
        );
    }

    return (
        <div className={styles['checkout-container']}>
            <h1>Finalizar compra</h1>

            <div className={styles['checkout-grid']}>
                <form className={styles['checkout-form']} onSubmit={handleSubmit}>
                    {error && <div className={styles['error-msg']}>{error}</div>}

                    <section className={styles['form-section']}>
                        <div className={styles['section-title']}>
                            <FaUserCheck />
                            <h3>Cliente fiel</h3>
                        </div>
                        <div className={styles['loyal-search']}>
                            <div className={styles['loyal-search-row']}>
                                <input
                                    type="text"
                                    value={familySearch}
                                    onChange={e => setFamilySearch(e.target.value)}
                                    placeholder="Buscar por DNI, nombre, email o telefono"
                                />
                                <button type="button" onClick={() => searchFamilyMember()} disabled={familySearch.trim().length < 2}>
                                    <FaSearch /> Buscar
                                </button>
                            </div>
                            {selectedMember && (
                                <div className={styles['selected-member']}>
                                    Usando datos de {selectedMember.nombre} {selectedMember.apellido}
                                    <button type="button" onClick={() => setSelectedMember(null)}>Quitar</button>
                                </div>
                            )}
                            {familyLoading && <div className={styles['helper-text']}>Buscando cliente...</div>}
                            {familyResults.length > 0 && (
                                <div className={styles['loyal-results']}>
                                    {familyResults.map(member => (
                                        <button type="button" key={member.dni} onClick={() => selectFamilyMember(member)}>
                                            <strong>{member.nombre} {member.apellido}</strong>
                                            <span>DNI {member.dni} - {member.email || member.telefono}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                            <p className={styles['helper-text']}>
                                Si todavia no sos cliente fiel, registrate desde la pestaña Cliente Fiel.
                            </p>
                        </div>
                        <button type="button" className={styles['loyal-register-btn']} onClick={openRegisterModal}>
                            Registrarme como cliente fiel
                        </button>
                    </section>

                    <section className={styles['form-section']}>
                        <div className={styles['section-title']}>
                            <h3>Datos del comprador</h3>
                        </div>
                        <div className={styles['form-row']}>
                            <div className={styles['form-group']}>
                                <label>DNI</label>
                                <input type="text" name="dni" value={formData.dni} required onChange={handleInputChange} inputMode="numeric" />
                            </div>
                            <div className={styles['form-group']}>
                                <label>Nombre completo</label>
                                <input type="text" name="nombre" value={formData.nombre} required onChange={handleInputChange} autoComplete="name" />
                            </div>
                        </div>
                        <div className={styles['form-row']}>
                            <div className={styles['form-group']}>
                                <label>Email</label>
                                <input type="email" name="email" value={formData.email} required onChange={handleInputChange} autoComplete="email" />
                            </div>
                            <div className={styles['form-group']}>
                                <label>Telefono</label>
                                <input type="tel" name="telefono" value={formData.telefono} required onChange={handleInputChange} autoComplete="tel" />
                            </div>
                        </div>
                    </section>

                    <section className={styles['form-section']}>
                        <div className={styles['section-title']}>
                            <h3>Entrega</h3>
                        </div>
                        <div className={styles['delivery-toggle']}>
                            <button
                                type="button"
                                className={isDelivery ? styles.active : ''}
                                onClick={() => handleDeliveryModeChange('Envio')}
                            >
                                <FaTruck /> Envio a domicilio
                            </button>
                            <button
                                type="button"
                                className={!isDelivery ? styles.active : ''}
                                onClick={() => handleDeliveryModeChange('Retiro')}
                            >
                                <FaStore /> Retiro por local
                            </button>
                        </div>

                        {isDelivery ? (
                            <>
                                <div className={styles['form-group']}>
                                    <label>Direccion de entrega</label>
                                    <div className={styles['address-combobox']}>
                                        <input
                                            type="text"
                                            name="direccion"
                                            value={formData.direccion}
                                            required
                                            onChange={handleInputChange}
                                            onFocus={() => setAddressFocused(true)}
                                            onBlur={() => window.setTimeout(() => setAddressFocused(false), 180)}
                                            placeholder="Ej.: Mendoza 1950, Rosario"
                                            autoComplete="off"
                                            role="combobox"
                                            aria-autocomplete="list"
                                            aria-expanded={addressSuggestions.length > 0}
                                            aria-controls="shipping-address-suggestions"
                                        />
                                        {addressFocused && addressSuggestions.length > 0 && (
                                            <ul id="shipping-address-suggestions" className={styles['address-suggestions']} role="listbox">
                                                {addressSuggestions.map(suggestion => (
                                                    <li key={suggestion.placeId}>
                                                        <button type="button" onMouseDown={() => selectAddressSuggestion(suggestion)}>
                                                            <strong>{suggestion.mainText || suggestion.description}</strong>
                                                            {suggestion.secondaryText && <span>{suggestion.secondaryText}</span>}
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                    <p className={styles['helper-text']}>Elegi una calle del nomenclador municipal e ingresa la altura. Si no aparece, completa la direccion y selecciona la zona manualmente.</p>
                                    {addressLookupMessage && <p className={styles['helper-text']}>{addressLookupMessage}</p>}
                                    {detectedZone && (
                                        <p className={styles['field-success']}>
                                            Zona detectada: {detectedZone.mapZoneName}
                                        </p>
                                    )}
                                </div>
                                <div className={styles['form-group']}>
                                    <label htmlFor="piso-departamento">Piso / departamento (opcional)</label>
                                    <input
                                        id="piso-departamento"
                                        type="text"
                                        name="pisoDepartamento"
                                        value={formData.pisoDepartamento}
                                        onChange={handleInputChange}
                                        placeholder="Ej.: Piso 3, Depto. B"
                                        autoComplete="address-line2"
                                    />
                                    <p className={styles['helper-text']}>Se agregara a la direccion seleccionada al confirmar el pedido.</p>
                                </div>
                                <div className={styles['form-group']}>
                                    <label>Zona de envio</label>
                                    <select name="envioId" value={formData.envioId} required onChange={handleInputChange}>
                                        <option value="">Seleccionar zona...</option>
                                        {SHIPPING_ZONES.filter(zone => zone.id !== 'gratis').map(zone => (
                                            <option key={zone.id} value={zone.id}>
                                                {zone.name} - ${zone.cost.toLocaleString()} (envío gratis a partir de ${zone.minPurchaseForFree.toLocaleString()})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </>
                        ) : (
                            <div className={styles['form-group']}>
                                <label>Local de retiro</label>
                                <select name="id_local" value={formData.id_local} required onChange={handleInputChange}>
                                    <option value="">Seleccionar local...</option>
                                    {pickupLocales.map(local => (
                                        <option key={local.locales_id} value={local.locales_id}>
                                            {local.nombre_local}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div className={styles['form-row']}>
                            <div className={styles['form-group']}>
                                <label>{isDelivery ? 'Fecha de entrega' : 'Fecha de retiro'}</label>
                                <input
                                    type="date"
                                    name="fechaEntrega"
                                    value={formData.fechaEntrega}
                                    min={minDeliveryDate}
                                    required
                                    disabled={isDelivery && !formData.envioId}
                                    onChange={handleInputChange}
                                />
                                <p className={styles['helper-text']}>
                                    {isDelivery
                                        ? (formData.envioId
                                            ? deliveryScheduleDescription
                                            : 'Primero elegí o detectá la zona de envío.')
                                        : 'No realizamos retiros los domingos.'}
                                </p>
                            </div>
                            <div className={styles['form-group']}>
                                <label>Horario</label>
                                <select
                                    name="horarioEntrega"
                                    value={formData.horarioEntrega}
                                    required
                                    disabled={isDelivery && (!formData.envioId || !formData.fechaEntrega || availableTimeRanges.length === 0)}
                                    onChange={handleInputChange}
                                >
                                    <option value="">
                                        {isDelivery && !formData.fechaEntrega ? 'Elegí primero una fecha...' : 'Seleccionar rango...'}
                                    </option>
                                    {availableTimeRanges.map(range => (
                                        <option key={range} value={range}>{range}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </section>

                    <section className={styles['form-section']}>
                        <div className={styles['section-title']}>
                            <h3>Pago</h3>
                        </div>
                        <div className={styles['form-group']}>
                            <label>Metodo de pago</label>
                            <select name="metodoPago" value={formData.metodoPago} required onChange={handleInputChange}>
                                <option value="Efectivo">Efectivo</option>
                                <option value="Transferencia">Transferencia bancaria</option>
                                <option value="Payway">Tarjeta de debito o credito</option>
                            </select>
                        </div>
                        {formData.metodoPago === 'Transferencia' && (
                            <div className={styles['receipt-upload']}>
                                <div className={styles['info-box']}>
                                    Adjunta el comprobante de la transferencia para confirmar el pedido como pagado.
                                </div>
                                <div className={styles['form-group']}>
                                    <label>Banco o referencia</label>
                                    <input type="text" name="banco" value={formData.banco} onChange={handleInputChange} placeholder="Opcional" />
                                </div>
                                <label className={styles['file-upload-label']}>
                                    <FaPaperclip />
                                    <span>{transferReceipt ? transferReceipt.name : 'Adjuntar comprobante'}</span>
                                    <input
                                        type="file"
                                        accept=".jpg,.jpeg,.png,.webp,.pdf,image/jpeg,image/png,image/webp,application/pdf"
                                        onChange={handleReceiptChange}
                                        required={formData.metodoPago === 'Transferencia'}
                                    />
                                </label>
                                <p className={styles['helper-text']}>
                                    Formatos permitidos: JPG, PNG, WEBP o PDF. Tamano maximo: 6 MB.
                                </p>
                                {transferReceiptError && (
                                    <p className={styles['field-error']}>{transferReceiptError}</p>
                                )}
                                {transferReceipt && transferReceiptUploadedAt && !transferReceiptError && (
                                    <p className={styles['field-success']}>
                                        Archivo subido el {transferReceiptUploadedAt}.
                                    </p>
                                )}
                            </div>
                        )}
                        {formData.metodoPago === 'Payway' && (
                            <div ref={cardFormRef} className={styles['payway-card']}>
                                <div className={styles['form-group']}>
                                    <label>Tipo de tarjeta</label>
                                    <select name="card_type" value={card.card_type} onChange={handleCardChange} required>
                                        <option value="">Seleccionar</option>
                                        <option value="debit">Debito</option>
                                        <option value="credit">Credito</option>
                                    </select>
                                </div>
                                <div className={styles['form-group']}>
                                    <label>Numero de tarjeta</label>
                                    <input type="text" inputMode="numeric" autoComplete="cc-number"
                                        data-decidir="card_number" name="card_number"
                                        value={card.card_number} onChange={handleCardChange}
                                        placeholder="1234 5678 9012 3456" maxLength={19} />
                                </div>
                                <div className={styles['form-group']}>
                                    <label>Nombre como figura en la tarjeta</label>
                                    <input type="text" autoComplete="cc-name"
                                        data-decidir="card_holder_name" name="card_holder_name"
                                        value={card.card_holder_name} onChange={handleCardChange}
                                        placeholder="TITULAR" />
                                </div>
                                <div className={styles['form-row']}>
                                    <div className={styles['form-group']}>
                                        <label>Mes</label>
                                        <input type="text" inputMode="numeric" autoComplete="cc-exp-month"
                                            data-decidir="card_expiration_month" name="card_expiration_month"
                                            value={card.card_expiration_month} onChange={handleCardChange}
                                            placeholder="12" maxLength={2} />
                                    </div>
                                    <div className={styles['form-group']}>
                                        <label>Año</label>
                                        <input type="text" inputMode="numeric" autoComplete="cc-exp-year"
                                            data-decidir="card_expiration_year" name="card_expiration_year"
                                            value={card.card_expiration_year} onChange={handleCardChange}
                                            placeholder="30" maxLength={2} />
                                    </div>
                                    <div className={styles['form-group']}>
                                        <label>CVV</label>
                                        <input type="text" inputMode="numeric" autoComplete="cc-csc"
                                            data-decidir="security_code" name="security_code"
                                            value={card.security_code} onChange={handleCardChange}
                                            placeholder="123" maxLength={4} />
                                    </div>
                                </div>
                                <div className={styles['form-row']}>
                                    <div className={styles['form-group']}>
                                        <label>Tipo doc.</label>
                                        <select data-decidir="card_holder_doc_type" defaultValue="dni">
                                            <option value="dni">DNI</option>
                                            <option value="cuil">CUIL</option>
                                            <option value="cuit">CUIT</option>
                                        </select>
                                    </div>
                                    <div className={styles['form-group']}>
                                        <label>Documento del titular</label>
                                        <input type="text" inputMode="numeric"
                                            data-decidir="card_holder_doc_number" name="card_holder_doc_number"
                                            value={card.card_holder_doc_number} onChange={handleCardChange}
                                            placeholder="27859328" />
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className={styles['form-group']}>
                            <label>Observaciones</label>
                            <textarea
                                name="observaciones"
                                value={formData.observaciones}
                                onChange={handleInputChange}
                                rows={3}
                                placeholder="Aclaraciones de entrega, cortes, horarios o referencias"
                            />
                        </div>
                    </section>

                    <button type="submit" className={styles['checkout-btn']} disabled={isSubmitting}>
                        {isSubmitting ? 'Procesando...' : 'Confirmar pedido'}
                    </button>
                </form>

                <aside className={styles['order-summary']}>
                    <h3>Resumen del pedido</h3>
                    <div className={styles['summary-items']}>
                        {cart.map((item, idx) => {
                            const imageUrl = getProductImageUrls(item)[0];
                            const itemKey = item.cartKey || `${item.idProductos}-${idx}`;
                            return (
                                <div key={itemKey} className={styles['summary-item']}>
                                    {imageUrl ? (
                                        <img loading="lazy" decoding="async" src={imageUrl} alt={item.NombresProductos} className={styles['summary-img']} />
                                    ) : (
                                        <div className={styles['summary-img-placeholder']}>{item.NombresProductos.charAt(0)}</div>
                                    )}
                                    <div className={styles['summary-product']}>
                                        <div className={styles['summary-product-header']}>
                                            <strong>{item.NombresProductos}</strong>
                                            <span className={styles['summary-price']}>${(item.Precio * item.quantity).toLocaleString()}</span>
                                        </div>
                                        <div className={styles['summary-product-actions']}>
                                            <div className={styles['quantity-controls']}>
                                                <button
                                                    type="button"
                                                    onClick={() => item.quantity === 1
                                                        ? window.confirm('Eliminar este producto?') && decreaseQuantity(itemKey)
                                                        : decreaseQuantity(itemKey)
                                                    }
                                                    className={styles['qty-btn']}
                                                    aria-label={`Restar ${item.NombresProductos}`}
                                                >
                                                    <FaMinus size={10} />
                                                </button>
                                                <span className={styles['qty-val']}>{item.quantity}</span>
                                                <button type="button" onClick={() => addToCart(item)} className={styles['qty-btn']} aria-label={`Sumar ${item.NombresProductos}`}>
                                                    <FaPlus size={10} />
                                                </button>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => window.confirm('Eliminar este producto?') && removeFromCart(itemKey)}
                                                className={styles['delete-item-btn']}
                                                aria-label={`Eliminar ${item.NombresProductos}`}
                                                title="Eliminar producto"
                                            >
                                                <FaTrash size={14} />
                                            </button>
                                        </div>
                                        {item.promo_selecciones && item.promo_selecciones.length > 0 && (
                                            <ul className={styles['promo-selection-list']}>
                                                {item.promo_selecciones.map((selection, selectionIdx) => (
                                                    <li key={`${selection.id_producto}-${selectionIdx}`}>
                                                        {selection.grupo ? `${selection.grupo}: ` : ''}{selection.cantidad}x {selection.nombre}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div className={styles['delivery-summary']}>
                        {isDelivery ? (
                            <span><FaTruck /> Envio a domicilio</span>
                        ) : (
                            <span><FaStore /> Retiro en {selectedLocal?.nombre_local || 'local a definir'}</span>
                        )}
                    </div>
                    <div className={styles['summary-row']}>
                        <span>Subtotal</span>
                        <span>${subtotal.toLocaleString()}</span>
                    </div>
                    <div className={styles['summary-row']}>
                        <span>{isDelivery ? 'Envio' : 'Retiro'}</span>
                        <span>{shippingCost > 0 ? `$${shippingCost.toLocaleString()}` : '$0'}</span>
                    </div>
                    <div className={styles['summary-total']}>
                        <span>Total</span>
                        <span>${total.toLocaleString()}</span>
                    </div>
                </aside>
            </div>

            {registerModalOpen && (
                <div className={styles['loyal-modal-backdrop']} role="dialog" aria-modal="true" aria-labelledby="loyal-modal-title">
                    <form className={styles['loyal-modal']} onSubmit={handleRegisterSubmit}>
                        <h2 id="loyal-modal-title">Cliente fiel</h2>
                        {registerError && <div className={styles['error-msg']}>{registerError}</div>}
                        <div className={styles['form-row']}>
                            <div className={styles['form-group']}>
                                <label>DNI</label>
                                <input name="dni" value={registerForm.dni || ''} onChange={handleRegisterChange} required inputMode="numeric" />
                            </div>
                            <div className={styles['form-group']}>
                                <label>Nombre</label>
                                <input name="nombre" value={registerForm.nombre || ''} onChange={handleRegisterChange} required />
                            </div>
                        </div>
                        <div className={styles['form-row']}>
                            <div className={styles['form-group']}>
                                <label>Apellido</label>
                                <input name="apellido" value={registerForm.apellido || ''} onChange={handleRegisterChange} required />
                            </div>
                            <div className={styles['form-group']}>
                                <label>Telefono</label>
                                <input name="telefono" value={registerForm.telefono || ''} onChange={handleRegisterChange} required />
                            </div>
                        </div>
                        <div className={styles['form-group']}>
                            <label>Email</label>
                            <input name="email" type="email" value={registerForm.email || ''} onChange={handleRegisterChange} required />
                        </div>
                        <div className={styles['form-group']}>
                            <label>Direccion</label>
                            <input name="direccion" value={registerForm.direccion || ''} onChange={handleRegisterChange} />
                        </div>
                        <div className={styles['form-row']}>
                            <div className={styles['form-group']}>
                                <label>Nacimiento</label>
                                <input name="fecha_nacimiento" type="date" value={registerForm.fecha_nacimiento || ''} onChange={handleRegisterChange} />
                            </div>
                            <div className={styles['form-group']}>
                                <label>Sexo</label>
                                <select name="sexo" value={registerForm.sexo || ''} onChange={handleRegisterChange}>
                                    <option value="">Seleccionar</option>
                                    <option value="Masculino">Masculino</option>
                                    <option value="Femenino">Femenino</option>
                                    <option value="Otro">Otro</option>
                                </select>
                            </div>
                        </div>
                        <div className={styles['loyal-modal-actions']}>
                            <button type="button" onClick={() => setRegisterModalOpen(false)} disabled={registerLoading}>
                                Cancelar
                            </button>
                            <button type="submit" disabled={registerLoading}>
                                {registerLoading ? 'Guardando...' : 'Registrarme'}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default CheckoutPage;